
import img0 from './Images/Login.png';
import img1 from './Images/Connect_Database.png';
import img2 from './Images/Add_Select_Database.png';
import img3 from './Images/Add_Database.png';
import img4 from './Images/Assign_Schemas.png';
import img5 from './Images/Upload_Schema.png';
import img6 from './Images/Schema_Only_Backup.png';
import img7 from './Images/Encryption_Settings.png';
import img8 from './Images/Encryption_In_Progress.png';
import img9 from './Images/Encryption_Completed.png';
import img10 from './Images/Encryption_Failed.png';
import img11 from './Images/Device_Code_Workflow.png';
import img12 from './Images/Device_Verification.png';
import img13 from './Images/Device_Consent.png';
import img14 from './Images/Console_Menu.png';
import img15 from './Images/Database_Connectivity.png';
import img16 from './Images/Processing_Results.png';
  
const content = `# Use OnData Encryption Tool

On Windows operating systems, you can choose to run the GUI version of the OnData Encryption Tool with easy interaction, or the command line version that is more 
desired for automation without human interaction. On Linux operating systems, you can run the OnData Encryption Tool from a Linux Shell. 

## 1. Run GUI Version of OnData Encryption Tool on Windows
After your computer is registered as an OnData client, you can launch the GUI version of the OnData Encryption Tool from either the Desktop shortcut or from the 
Windows Start menu. The OnData Encryption Tool provides a wizard that goes through the following steps:
- Login
- Connect to Database
- Add Database to OnData Portal
- Add or Select Database
- Update Database Schema
- Schema Only Backup
- Encryption Settings
- Operation In Progress
- Operation Completed
  
The instructions for each step are illustrated below.  

**Note**: The database encryption process may take minutes or hours, depending on the size of the database. You may adjust the power 
management settings of your computer so that it will not enter sleep or hibernate mode during processing.  
  
### 1.1 Login
To get started, please click on the \`Login\` button to login to the OnData platform. Use the same credentials as what you would use to login to the 
OnData Portal.  

![Login Screen](${ img0 })  

### 1.2 Connect to Database

Select the database engine and enter the connection information to connect to the database.

![Connect to Database](${ img1 })

There are two options to complete the database connection information:

- \`Use existing\`: Use existing connectivity information of a database that was successfully connected to in the past. The options are sorted by 
database names. You may still need to fill in account credentials because such credentials are not cached for security purpose.
- \`Create new\`: Create a new entry of database connectivity information. Once connected successfully, the database connectivity information will be 
cached locally for future use. If connectivity information for a database is cached before, such connectivity information will be updated. 

**Note**: Any password associated with the database connectivity will never be cached for security purpose.

If you choose to create a new entry of database connectivity information, you may select the following options based on the database type:
- Azure Cosmos DB
  - Database Name: The name of the database to connect to
  - Endpoint URL: In the \`Settings\` section of the Azure Cosmos DB account, select \`Keys\`, then select "Copy" to copy the \`URI\` to this field
  - Authorization Key: In the \`Settings\` section of the Azure Cosmos DB account, select \`Keys\`, then select "Copy" to copy the \`PRIMARY KEY\` to this field
- DB2
  - Server Address: The IP address or Fully Qualified Domain Name (FQDN) of the DB2 database server
  - Port: The Port number for the DB2 database server. The default is 25000
  - Database Name: The name of the database to connect to
  - Username: The username used to connect to the database. The user must have sufficient read and write permissions to the database
  - Password: The password for the database user
- MariaDB
  - Server Address: The IP address or Fully Qualified Domain Name (FQDN) of the MariaDB database server
  - Port: The Port number for the MariaDB database server. The default is 3306
  - Database Name: The name of the database to connect to
  - Username: The username used to connect to the database. The user must have sufficient read and write permissions to the database
  - Password: The password for the database user
- MongoDB
  - Server Address: The IP address or Fully Qualified Domain Name (FQDN) of the MongoDB database server
  - Port: The Port number for the MongoDB database server. The default is 27017
  - Database Name: The name of the database to connect to
  - User Type: Whether the user is a Global User or a Database User 
  - Username: The username used to connect to the database. The user must have sufficient read and write permissions to the database
  - Password: The password for the database user
- MySQL
  - Server Address: The IP address or Fully Qualified Domain Name (FQDN) of the MySQL database server
  - Port: The Port number for the MySQL database server. The default is 3306
  - Database Name: The name of the database to connect to
  - Username: The username used to connect to the database. The user must have sufficient read and write permissions to the database
  - Password: The password for the database user
- Oracle  
  There is an option to use TNS or not for Oracle. If TNS is used, fill in the following information:
  - TNS ADMIN: A directory where the SQL*Net configuration files (sqlnet.ora and tnsnames.ora) are located. This is optional. If left empty, it will 
  use the TNS_ADMIN environment variable to find the configuration files
  - TNS Service Name: The name of the TNS service for connecting to the database server
  - Database Name: The name of the database to connect to
  - Username: The username used to connect to the database. The user must have sufficient read and write permissions to the database
  - Password: The password for the database user
  
  If TNS is not used, fill in the following information:
  - Server Address: The IP address or Fully Qualified Domain Name (FQDN) of the Oracle database server
  - Port: The Port number for the Oracle database server. The default is 1521
  - Database Name: The name of the database to connect to
  - Username: The username used to connect to the database. The user must have sufficient read and write permissions to the database
  - Password: The password for the database user
- PostgreSQL
  - Server Address: The IP address or Fully Qualified Domain Name (FQDN) of the PostgreSQL database server
  - Port: The Port number for the PostgreSQL database server. The default is 5432
  - Database Name: The name of the database to connect to
  - Username: The username used to connect to the database. The user must have sufficient read and write permissions to the database
  - Password: The password for the database user
- Snowflake
  - Host: The hostname of your Snowflake account, e.g. &lt;ACCOUNT&gt;.snowflakecomputing.com
  - Warehouse: The Snowflake Warehouse used for queries
  - Database Name: The name of the database to connect to
  - Username: The username used to connect to the database. The user must have sufficient read and write permissions to the database
  - Password: The password for the database user
- SQL Server
  - Server Address: The IP address or Fully Qualified Domain Name (FQDN) of the MS SQL database server
  - Port: The Port number for the MS SQL database server. The default is 1433
  - Database Name: The name of the database to connect to
  
  If \`Integrated Security\` is checked, the security context of the current logon user will be used to connect to the SQL database. If \`Integrated Security\` 
  is unchecked, you will need to provide the following account credentials to connect to the MS SQL database:
  - Username: The username used to connect to the database. The user must have sufficient read and write permissions to the database
  - Password: The password for the database user
  
After you fill in the database connectivity information, you may select the \`Test\` button to test the database connectivity and update the 
connectivity information if the database cannot be connected.

Select \`Connect\` to connect to the database. Depending on whether database is already managed by OnData, you may be redirected to different pages:
  - If the database does not exist in OnData Portal, it will show the \`Add Database to OnData Portal\` page
  - If the database exists in OnData Portal, it will show the \`Update Database Schema\` page. **Note**: For an Oracle database, the tool will further check if the physical database name matches the logical database name used at the OnData Portal. If they do not match, the tool will show 
  the \`Add or Select Database\` page. 
  
![Add or Select Database](${ img2 })  
  
This scenario happens only when you have chosen to include only a subset of the database schemas from the physical database before. In this case, 
you are presented with two options:
  - Choose the \`Select Database\` option and select a previously created logic database from the \`OnData Database Name\` dropdown list and proceed to 
  the next steps
  - Choose the \`Add Database\` option and click on the \`Add\` button to add another logical database to the OnData Portal by selecting a different 
  subset of database schemas from the same physical database
  
### 1.3 Add Database to OnData Portal
This page allows you to add the database to the OnData Portal. You may select an Encryption Key and enter a description of the database. If your OnData 
account has the permissions to manage encryption keys, this page will allow you to create a new encryption key as needed. Clicking on the \`Add\` button 
to add the database to the OnData Portal. The tool will transition to the \`Upload Database Schema\` page.
  
![Add Database](${ img3 })  
  
**Note**: If the database engine is Oracle, you have the option of processing a subset of the schemas from the physical Oracle database. In that case, you can check 
the option of \`Specify Subset of Schemas\`. The OnData Encryption Tool will ask for a logical database name for the OnData Portal to represent this subset of 
schemas. Please fill in the logical database name in the \`OnData Database Name\` field, and assign a subset of schemas to this logical database. Clicking on the \`Add\` button 
to add the database to the OnData Portal. The tool will transition to the \`Upload Database Schema\` page.
  
![Assign Schemas](${ img4 })  
  
### 1.4 Upload Database Schema
This page displays a summary about the database and the encryption key to be used. You may choose any of the following options:
- Cancel: Go back to the \`Connect to Database\` page
- Upload: Go to the \`Schema Only Backup\` page and perform a schema-only backup for the database
    
![Upload Schema](${ img5 })  
  
Please note that when the database schema is captured and uploaded **the first time and the first time only**, OnData will automatically detect the sensitive 
info types that are configured and enabled under \`Settings -> Encryption -> Data Info Types\`. Any data fields matching those sensitive info types will be 
automatically classified and assigned with an encryption rule, based on the configuration of the corresponding info types.
  
### 1.5 Update Database Schema
This page shows information about the existing database, including the assigned Encryption Key, and allows you to update the database schema if 
preferred. You may choose any of the following options:
- Skip: Skip the schema-only backup step and go to the \`Encryption Settings\` page. Please choose this option if you are sure that the database 
schema has not been changed since last backup
- Cancel: Go back to the \`Connect to Database\` page
- Update: Go to the \`Schema Only Backup\` page and perform a schema-only backup for the database

### 1.6 Schema Only Backup
The schema-only backup process will be performed, with the relevant messages displayed inside the \`Status Message\` box. After the schema-only backup 
process is completed, you may choose any of the following options:
- Cancel: Go back to the \`Connect to Database\` page
- Next: Go to the \`Encryption Settings\` page
  
![Schema Only Backup](${ img6 })  
  
### 1.7 Encryption Settings
This page provides a direct link to the OnData Portal so that you can configure the encryption rules for the database. You may be required to login 
to the OnData Portal first. After the encryption rules are configured for all tables are columns that need to be protected, you may proceed with the 
processing of the database. 
  
![Encryption Settings](${ img7 })  
  
There are four operating modes for the OnData Encryption Tool:
- **Encryption**: This mode is used to encrypt the sensitive data in the database based on the encryption rules configured at the OnData Portal. Please 
note that the database must not be encrypted already. When the Encryption mode is used, and you are using this mode to generate test data, you may 
further choose the following two options:
  - Apply Obfuscation Rules: If an obfuscation rule is configured for a column, the obfuscation rule will be applied instead of applying encryption
  - Subsetting Enabled (this option is only available for Microsoft SQL Server, Oracle, and DB2 databases at this time): If you would like to enable the 
  subsetting feature when generating test data, you may choose this option. In that case, you will need to provide a database name to store the data subset 
  from the original database. A link to the  OnData Portal is provided for you to view and update the subsetting configurations. For more details on this feature, 
  please refer to [**Database Subsetting Feature**](reference_doc#securedb/ondata_encryption/database_subsetting). 
  
    **Warning**: Please do not select \`Apply Obfuscation Rules\` or \`Subsetting Enabled\` if you are encrypting a Production database. 
  
- **Decryption**: This mode is used to decrypt the previously encrypted data in the database. Please note that the database must be encrypted before.
- **Apply Encryption Setting Changes**: This mode is used to apply the encryption setting changes to a database in Non-Production or Production environment. 
- **Migrate and Encrypt Data**: This option is only available for Oracle databases. For more details on how to use this option to migrate and encrypt data from 
one Oracle database to another, please refer to [**Migrate and Encrypt Data for Oracle Databases**](reference_doc#securedb/ondata_encryption/migrate_and_encrypt_oracle_database). 

You may take any of the following actions on this page:
- \`Advanced Options\`: Allow to configure the advanced processing options, including threading, logging, and error handling. Please stay with the default 
options if you are not sure about the changes
  - \`Page Size\`: The number of rows to process for each processing chunk. Please note that for MongoDB, the page size is set to 100 and cannot be changed
  - \`Max Parallelism\`: The maximum number of concurrent tasks for the application. This is used for performance optimization
  - \`Threading Factor\`: The number of threads to start for each logical processor of the computer. This is used for performance optimization as well
  - \`DLL Log Level\`: The log level for the EncryptSQL module
  - \`Skip Invalid Input\`: If this option is checked, the OnData Encryption Tool will skip any errors and continue the processing without being interrupted 
  by errors

  - For relational databases, the following additional options are available:  
    - \`Log Error Data\`: If this option is checked, the error data will be logged to the log file. Please note that the error data may contain sensitive data. 
  You should only use this option for debugging purpose and delete the log files after you are done with debugging

  - For MongoDB databases, the following additional options are available:
    - \`Wait Queue Size\`: The maximum number of operations that can be queued up waiting to be executed on a particular MongoDB server or replica set. When the 
  number of incoming requests exceeds the available resources (such as the number of connections or the capacity to handle concurrent operations), these 
  requests are placed in a wait queue
    - \`Wait Queue Timeout\`: The maximum amount of time an operation will wait in the queue before it times out. When an operation is placed in the wait 
  queue due to resource limitations (such as all available connections being used), it will wait for a certain period specified by the wait queue 
  timeout setting. If the operation doesn't get a chance to execute within this timeout period, an error will be thrown
  
- \`Cancel\`: Go back to the \`Connect to Database\` page
- \`Proceed\`: Display a confirmation message to confirm the processing of the database. It is recommended that you have a backup of the database 
before proceeding with the operation. Click on the \`Confirm\` button to proceed to the next step. Depending on the selected operating mode, the 
tool will proceed to the \`Encryption In Progress\` page, the \`Decryption In Progress\` page, or the \`Apply Encryption Setting Changes\` page.
  
Please note that this step may take minutes or hours depending on the size of the database, number of columns and rows to be processed, and 
available hardware resources. You may adjust the power management settings of your computer so that it will not enter sleep or hibernate mode 
during processing.    
  
![Encryption In Progress](${ img8 })  
  
The \`Encryption In Progress\` page, the \`Decryption In Progress\` page, and the \`Apply Encryption Setting Changes\` page will show the relevant 
messages as the database is processed.  Once the processing is completed, the \`Done\` button 
is displayed at the bottom. A \`View Logs\` link is displayed on the page as well, so that you can click on it and go directly to the logs 
folder.
  
![Encryption Completed](${ img9 })  
  
## 1.8. Recover from Failure
If the above process fails, an error message will be displayed to indicate the reason for the failure. You may review the detailed error messages in the 
log files by following the \`View Logs\` link.
  
![Failed on Encryption](${ img10 })  
  
Once you find the root cause and resolve the issue, you can click on the \`Rerun\` button to resume from where the process has just stopped, without 
starting over the entire process. This option will save a lot of time if your database size is large. If the process fails again, you may use 
the \`Advanced Options\` to turn on more logging and/or skip invalid input if desired.  

Please note that this \`Rerun\` feature only works for relational databases, not for MongoDB databases.  
  

## 2. Run Command Line Version of OnData Encryption Tool on Windows
After your computer is registered as an OnData client, you can launch the command line version of the OnData Encryption Tool by starting a command line prompt, 
navigating to where the OnData Encryption Tool is installed, with default location of \`C:/Program Files/OnData/OnData Encryption Tool\`, and running the executable 
\`OnDataEncryptionTool.exe\` from the command line. The OnData Encryption Tool provides an interactive process to capture user input and perform the desired 
operations. The instructions for each step are illustrated below.  

**Note**: The database encryption process may take minutes or hours, depending on the size of the database. You may adjust the power management settings of your 
computer so that it will not enter sleep or hibernate mode during processing.  
  
### 2.1 Device Code Workflow
To authenticate and authorize the user to use the OnData Encryption Tool, the Device Code Workflow should be completed before any of the OnData Encryption Tool 
features can be used. The following instructions are provided after launching the OnData Encryption Tool from the command prompt.  
  
![Device Code Workflow](${ img11 })  
  
Launch a web browser and navigate to the designated URL (e.g., https://us.ondatahub.com/device) at the OnData Portal and enter the device code to authenticate.
  
![Device Verification](${ img12 })  
  
The OnData Portal may prompt you to login if you have not done so. Once logged in, if you have the permissions needed to run the OnData Encryption Tool, the OnData 
Portal will ask your consent to allow the OnData Encryption Tool to access your OnData account. Select the \`Yes, Allow\` option to proceed and go back to the OnData 
Encryption Tool.  
  
![Device Consent](${ img13 })  
  
### 2.2 Interactive Menu for Database Processing
After the Device Code Workflow is successfully completed, the OnData Encryption Tool provides an interactive menu for users to choose a particular task from a list 
of options, including \`Schema-Only Backup\`, \`Encrypt Database\`, \`Decrypt Database\`, \`Apply Encryption Setting Changes\`, and \`Exit\`. 
  
![Console Menu](${ img14 })  
  
Here are the details about each option:
- \`Schema-Only Backup\`: Backup the database schema, excluding the actual data, to the OnData platform. If a database has never been processed before and is not 
available at the OnData platform yet, you have to run this \`Schema-Only Backup\` step first. After the \`Schema-Only Backup\` step, you can go to the OnData Portal 
to configure the encryption settings, as well as obfuscation settings if desired.  
- \`Encrypt Database\`: Encrypt a database after you have performed \`Schema-Only Backup\` and configured the encryption settings at the OnData Portal. If you are 
using the OnData Encryption Tool for the test data generation use case, you may optionally configure the obfuscation settings at the OnData Portal as well, and 
choose to apply the obfuscation rules.  
- \`Decrypt Database\`: Decrypt a previously encrypted database.  
- \`Apply Encryption Setting Changes\`: Apply encryption setting changes to a Production database.  
- \`Exit\`: Exit from the OnData Encryption Tool.  

The OnData Encryption Tool will prompt you for database connectivity information. Please refer to \`Section 1.2 Connect to Database\` above on what information is 
needed in order to connect to a database.  
  
![Database Connectivity](${ img15 })  
  
After the OnData Encryption Tool successfully connects to the database, it will perform the desired task and output the internal processing steps and results to 
the command prompt console.  
  
![Sample Processing Result](${ img16 })  


## 3. Run OnData Encryption Tool on Linux for Schema Only Backup
Running the OnData Encryption Tool on Linux is similar to running the command line version of the tool on Windows. You can navigate to where the OnData Encryption 
Tool is installed, and run the executable \`OnDataEncryptionTool\` (or the \`db2-start-encryption-tool.sh\` script for DB2 databases) from the Linux Shell.  
  
The OnData Encryption Tool provides an interactive process to capture user input and perform the desired operations. Similarly, it will need to go through the Device 
Code Workflow for user authentication and authorization before an interactive menu is presented for user input. Please refer to \`Section 2\` above on how to use the 
OnData Encryption Tool.  
  
**Note**: Before you run the \`OnDataEncryptionTool\` or the \`db2-start-encryption-tool.sh\` script, you may need to grant the \`Execute\` permission 
under Linux so that they can be launched from the command line.  
  

## 4. Run OnData Encryption Tool on Linux for Encryption or Decryption
In order to run the OnData Encryption Tool for data encryption or decryption, you will need to first run the \`Configurator\` tool to register the OnData Encryption Tool, 
then run the executable \`OnDataEncryptionTool\` (or the \`db2-start-encryption-tool.sh\` script for DB2 databases) from the Linux Shell. To run the \`Configurator\` tool, 
please refer to \`Section 2. Client Registration on Linux\` of the [**OnData Client Registration**](reference_doc#securedb/database_drivers/ondata_client_registration) documentation.  
  
The OnData Encryption Tool provides an interactive process to capture user input and perform the desired operations. Similarly, it will need to go through the Device 
Code Workflow for user authentication and authorization before an interactive menu is presented for user input. Please refer to \`Section 2\` above on how to use the 
OnData Encryption Tool. You can perform data encryption and decryption once the OnData Encryption Tool is registered.  
  
**Note**: Before you run the \`Configurator\` tool, the \`OnDataEncryptionTool\`, or the \`db2-start-encryption-tool.sh\` script, you may need to grant the \`Execute\` permission 
under Linux so that they can be launched from the command line.  
  `
  
export default content;
